import type { Route } from "../../../utils/models/Route";
import { detectStageConnectors, haversineMeters, orderedDayStages } from "./stage-connectors";

/**
 * Stage destinations — pure logic.
 *
 * A stage destination attaches a day's END marker to a map item (map-data
 * POI, waypoint or hike highlight): the marker is moved onto the item's
 * coordinates and the stage badge renders as a ring around the item's icon.
 * An entry only counts as ATTACHED while the day still exists and its end
 * marker actually sits on the destination — drag the marker away and the
 * attachment dissolves.
 *
 * This module is deliberately side-effect free (no map, no store) so it can
 * be unit tested — rendering and drag behaviour live in
 * store/modules/actions/ (stage-connectors.ts / markers.ts).
 */

// A day's end marker further than this from its destination is DETACHED.
// Attaching places the marker exactly on the item, so this only needs to
// absorb floating-point drift — anything larger is a deliberate move.
export const DESTINATION_ATTACHED_THRESHOLD_M = 15;

export type StageDestinationItemType = 'poi' | 'waypoint' | 'highlight';

export interface StageDestination {
  itemType: StageDestinationItemType;
  /** poi: `${categoryType}:${id}` • waypoint/highlight: clientId */
  itemId: string;
  name: string;
  /** POI type string (POIUtility.fromString-compatible) — drives the icon */
  poiType: string;
  /** [lon, lat] of the item marker */
  coordinates: [number, number];
}

export interface StageEndPoint {
  dayNumber: number;
  routeKey: string;
  markerIndex: number;
  coordinates: [number, number];
}

/**
 * The END point of a day's stage: the last marker of the day's last
 * main-line route (including the final day, whose end is the trail finish).
 * Null when the day has no main-line route with a marker.
 */
export function stageEndForDay(
  routes: { [routeKey: string]: Route },
  dayNumber: number
): StageEndPoint | null {
  // minMarkers: 1 — a day's current end point is meaningful (and
  // attachable) as soon as its route has a first marker, even before it has
  // a second point to form a real line.
  const stage = orderedDayStages(routes, 1).find(s => s.dayNumber === dayNumber);
  if (!stage) return null;
  const markerIndex = stage.lastRoute.markers.length - 1;
  const coords = stage.lastRoute.markers[markerIndex]?.markerGeometry?.coordinates;
  if (!coords) return null;
  return {
    dayNumber,
    routeKey: stage.lastKey,
    markerIndex,
    coordinates: [coords[0], coords[1]],
  };
}

/** Day numbers that have a stage end a destination could attach to. */
export function attachableDayNumbers(routes: { [routeKey: string]: Route }): number[] {
  return orderedDayStages(routes, 1)
    .filter(stage => stage.lastRoute.markers.length > 0)
    .map(stage => stage.dayNumber);
}

/**
 * Is this marker a stage END (the drag handle a destination can attach to)?
 * Returns the day number when it is, null otherwise.
 */
export function stageEndDayForMarker(
  routes: { [routeKey: string]: Route },
  routeKey: string,
  markerIndex: number
): number | null {
  const stage = orderedDayStages(routes, 1).find(s => s.lastKey === routeKey);
  if (!stage) return null;
  if (markerIndex !== stage.lastRoute.markers.length - 1) return null;
  return stage.dayNumber;
}

export interface StageStartPoint {
  dayNumber: number;
  routeKey: string;
  markerIndex: 0;
  coordinates: [number, number];
}

/**
 * The START point of a given day's stage: the first marker of that day's
 * first main-line route (mirrors stageEndForDay). "Set as start point"
 * targets whichever day is currently active — a later day can have its own
 * independent start when it isn't chained onto the previous day's end — not
 * necessarily the plan's overall first day.
 *
 * minMarkers: 1 — same reasoning as stageEndForDay: a day's current start
 * point is meaningful (and attachable) as soon as its route has a first
 * marker. Null when the day has no main-line route with a marker.
 */
export function stageStartForDay(
  routes: { [routeKey: string]: Route },
  dayNumber: number
): StageStartPoint | null {
  const stage = orderedDayStages(routes, 1).find(s => s.dayNumber === dayNumber);
  if (!stage) return null;
  const coords = stage.firstRoute.markers[0]?.markerGeometry?.coordinates;
  if (!coords) return null;
  return { dayNumber, routeKey: stage.firstKey, markerIndex: 0, coordinates: [coords[0], coords[1]] };
}

/**
 * Is this marker a stage START (the drag handle a destination can attach
 * to)? Returns the day number when it is, null otherwise. Mirrors
 * stageEndDayForMarker.
 */
export function stageStartDayForMarker(
  routes: { [routeKey: string]: Route },
  routeKey: string,
  markerIndex: number
): number | null {
  if (markerIndex !== 0) return null;
  const stage = orderedDayStages(routes, 1).find(s => s.firstKey === routeKey);
  return stage ? stage.dayNumber : null;
}

export interface AttachedTrailStart {
  destination: StageDestination;
  routeKey: string;
  markerIndex: 0;
}

/**
 * Is the trail-start destination still attached to its recorded item —
 * i.e. does SOME day's stage-start currently sit on it (within
 * DESTINATION_ATTACHED_THRESHOLD_M)? Searches every day rather than
 * assuming the plan's first day, since attachTrailStart can target any
 * day's empty route, not just day 1's.
 */
export function resolveAttachedTrailStart(
  routes: { [routeKey: string]: Route },
  destination: StageDestination | null
): AttachedTrailStart | null {
  if (!destination) return null;
  for (const stage of orderedDayStages(routes, 1)) {
    const start = stageStartForDay(routes, stage.dayNumber);
    if (!start) continue;
    if (haversineMeters(start.coordinates, destination.coordinates) <= DESTINATION_ATTACHED_THRESHOLD_M) {
      return { destination, routeKey: start.routeKey, markerIndex: start.markerIndex };
    }
  }
  return null;
}

export interface AttachedStageDestination {
  dayNumber: number;
  destination: StageDestination;
  routeKey: string;
  markerIndex: number;
  /** Same value as dayNumber — kept as its own field to match the numbered
   *  labels on the map's stage markers and connectors (StageConnector /
   *  StageEndMarker's stageNumber). */
  stageNumber: number;
}

/**
 * Filter raw destination state down to the entries that are actually
 * attached: the day's stage still exists and its end marker sits on the
 * destination (within DESTINATION_ATTACHED_THRESHOLD_M). Everything else is
 * stale — the day was deleted or the marker was dragged away.
 */
export function resolveAttachedDestinations(
  routes: { [routeKey: string]: Route },
  destinations: { [dayNumber: number]: StageDestination }
): { [dayNumber: number]: AttachedStageDestination } {
  const attached: { [dayNumber: number]: AttachedStageDestination } = {};
  for (const key of Object.keys(destinations)) {
    const dayNumber = Number(key);
    const destination = destinations[dayNumber];
    if (!destination) continue;
    const stageEnd = stageEndForDay(routes, dayNumber);
    if (!stageEnd) continue;
    if (haversineMeters(stageEnd.coordinates, destination.coordinates) > DESTINATION_ATTACHED_THRESHOLD_M) continue;
    attached[dayNumber] = {
      dayNumber,
      destination,
      routeKey: stageEnd.routeKey,
      markerIndex: stageEnd.markerIndex,
      stageNumber: dayNumber,
    };
  }
  return attached;
}

/**
 * Re-key stage destinations AND the trail-start destination together after
 * the itinerary geometry has changed (e.g. a full or partial reverse), so
 * attachments survive instead of being pruned.
 *
 * The attached item (POI/waypoint/highlight) never moves — only which point
 * of the trail (the overall start, or a day's stage end) now lands on it
 * does. A reversal can move a destination BETWEEN the trail-start slot and
 * the day-keyed stage-end pool (e.g. the old trail start becomes the new
 * last day's end, and the old last day's end becomes the new trail start),
 * so both slots must be re-matched as one combined pool — remapping only
 * the stage-end pool (as an earlier version of this function did) leaves
 * the trail-start slot stale and lets the render-time prune delete it.
 *
 * For each recorded destination we find whichever CURRENT anchor (the trail
 * start, or some day's stage end) now sits on its coordinates (within
 * DESTINATION_ATTACHED_THRESHOLD_M) and re-key it there. An entry that
 * matches nothing keeps its original slot (the render-time prune will drop
 * it, matching pre-existing behaviour for a destination the user genuinely
 * detached).
 *
 * Must be called AFTER the routes have been mutated to their new geometry.
 * Pure: returns new objects, does not mutate its inputs.
 */
export function remapDestinationsAfterReversal(
  routes: { [routeKey: string]: Route },
  stageDestinations: { [dayNumber: number]: StageDestination },
  trailStartDestination: StageDestination | null
): {
  stageDestinations: { [dayNumber: number]: StageDestination };
  trailStartDestination: StageDestination | null;
} {
  const dayNumbers = attachableDayNumbers(routes);

  // A day's start that's CONNECTED to the previous day's end is just that
  // junction point wearing two hats — it isn't a genuinely independent
  // "start" a destination could have been attached to, so it must not count
  // here. Without this exclusion every internal junction would spuriously
  // match as "a trail start", stealing destinations that are really just
  // some day's end (or vice versa on the way back).
  const connectedStartRouteKeys = new Set(detectStageConnectors(routes).map(c => c.nextRouteKey));

  const matchesTrailStart = (destination: StageDestination): boolean =>
    dayNumbers.some(dayNumber => {
      const start = stageStartForDay(routes, dayNumber);
      if (!start || connectedStartRouteKeys.has(start.routeKey)) return false;
      return haversineMeters(start.coordinates, destination.coordinates) <= DESTINATION_ATTACHED_THRESHOLD_M;
    });

  const findMatchedDay = (destination: StageDestination): number | null => {
    for (const dayNumber of dayNumbers) {
      const stageEnd = stageEndForDay(routes, dayNumber);
      if (!stageEnd) continue;
      if (haversineMeters(stageEnd.coordinates, destination.coordinates) <= DESTINATION_ATTACHED_THRESHOLD_M) {
        return dayNumber;
      }
    }
    return null;
  };

  const remappedStageDestinations: { [dayNumber: number]: StageDestination } = {};
  let remappedTrailStart: StageDestination | null = null;

  // Every day-N start coincides with day-(N-1)'s end when the two stages are
  // connected, so checking "is this a day's start" first would make a plain
  // end incorrectly resolve as a start at every internal junction. An item
  // that WAS an end keeps that identity unless no day's end matches it
  // anymore — only then does it fall back to the start pool (the genuine
  // end↔start crossover a reversal can cause).
  for (const key of Object.keys(stageDestinations)) {
    const originalDay = Number(key);
    const destination = stageDestinations[originalDay];
    if (!destination) continue;
    const matchedDay = findMatchedDay(destination);
    if (matchedDay !== null) {
      remappedStageDestinations[matchedDay] = destination;
      continue;
    }
    if (matchesTrailStart(destination)) {
      remappedTrailStart = destination;
      continue;
    }
    remappedStageDestinations[originalDay] = destination;
  }

  if (trailStartDestination) {
    if (matchesTrailStart(trailStartDestination)) {
      remappedTrailStart = trailStartDestination;
    } else {
      const matchedDay = findMatchedDay(trailStartDestination);
      if (matchedDay !== null) {
        remappedStageDestinations[matchedDay] = trailStartDestination;
      } else {
        // No match anywhere — leave it in the trail-start slot; the
        // render-time prune (resolveAttachedTrailStart) will drop it,
        // matching pre-existing behaviour for a genuinely stale entry.
        remappedTrailStart = trailStartDestination;
      }
    }
  }

  return { stageDestinations: remappedStageDestinations, trailStartDestination: remappedTrailStart };
}

/** The raw per-day attachment fields as persisted on HikeDay (see hike_days_attributes). */
export interface PersistedHikeDayAttachment {
  dayNumber: number;
  startItemType?: string | null;
  startItemId?: string | null;
  startItemName?: string | null;
  startItemPoiType?: string | null;
  startItemLon?: number | null;
  startItemLat?: number | null;
  endItemType?: string | null;
  endItemId?: string | null;
  endItemName?: string | null;
  endItemPoiType?: string | null;
  endItemLon?: number | null;
  endItemLat?: number | null;
}

/** The item's persisted position, when the row has one. */
function persistedItemCoord(
  lon: number | null | undefined,
  lat: number | null | undefined
): [number, number] | null {
  if (typeof lon !== 'number' || typeof lat !== 'number') return null;
  if (!Number.isFinite(lon) || !Number.isFinite(lat)) return null;
  return [lon, lat];
}

/**
 * Rebuild stageDestinations/trailStartDestination from the persisted
 * per-day attachment fields plus the just-hydrated routes, when opening a
 * saved hike for editing.
 *
 * The item's position is used when the row has one. Rows written before those
 * columns existed fall back to the day's own current stage end / trail start,
 * which is where an attachment sits by construction — but that fallback
 * cannot detect drift: a client with no notion of attachments (an older mobile
 * build) can move the marker while leaving the item columns untouched, and
 * re-deriving the position from the marker then relabels wherever it landed.
 * With a real position, the render-time prune
 * (resolveAttachedDestinations / resolveAttachedTrailStart) sees the gap and
 * drops the stale pin instead.
 *
 * The trail start is still a single slot (not one entry per day), but it can
 * now be attached to ANY day's stage-start, not just day 1's — so which
 * HikeDay row it lives on is found directly from startItemType/startItemId
 * (whichever day has them set) rather than assumed to be the plan's first
 * day, mirroring how the save-side getter (getHikeDaysAttributes) derives
 * the day from the attached route's current `.day` rather than hardcoding 1.
 *
 * Pure: returns new objects, does not mutate its inputs.
 */
export function hydrateStageDestinationsFromHikeDays(
  routes: { [routeKey: string]: Route },
  hikeDays: PersistedHikeDayAttachment[]
): {
  stageDestinations: { [dayNumber: number]: StageDestination };
  trailStartDestination: StageDestination | null;
} {
  const stageDestinations: { [dayNumber: number]: StageDestination } = {};
  for (const day of hikeDays) {
    if (!day.endItemType || !day.endItemId) continue;
    const persisted = persistedItemCoord(day.endItemLon, day.endItemLat);
    const stageEnd = stageEndForDay(routes, day.dayNumber);
    // Pre-coordinates rows still need the day's own end to stand in; with
    // nothing to fall back on there's no position to attach to at all.
    if (!persisted && !stageEnd) continue;
    stageDestinations[day.dayNumber] = {
      itemType: day.endItemType as StageDestinationItemType,
      itemId: day.endItemId,
      name: day.endItemName || '',
      poiType: day.endItemPoiType || '',
      coordinates: persisted ?? stageEnd!.coordinates,
    };
  }

  let trailStartDestination: StageDestination | null = null;
  const startDay = hikeDays.find(d => d.startItemType && d.startItemId);
  if (startDay?.startItemType && startDay?.startItemId) {
    const persisted = persistedItemCoord(startDay.startItemLon, startDay.startItemLat);
    const start = stageStartForDay(routes, startDay.dayNumber);
    if (persisted || start) {
      trailStartDestination = {
        itemType: startDay.startItemType as StageDestinationItemType,
        itemId: startDay.startItemId,
        name: startDay.startItemName || '',
        poiType: startDay.startItemPoiType || '',
        coordinates: persisted ?? start!.coordinates,
      };
    }
  }

  return { stageDestinations, trailStartDestination };
}

/**
 * Split a POI attachment's `itemId` back into the parts the POI dialog needs.
 *
 * extractAttachTarget stores it as `${categoryType}:${id}`, and a category can
 * itself contain no colon, so the FIRST colon is the separator — splitting on
 * the last would break `Accommodation:43` the moment an id ever contained one.
 * Returns null for a malformed or non-POI id rather than guessing.
 */
export function parsePoiItemId(itemId: string | null | undefined): { categoryType: string; poiId: string } | null {
  if (!itemId) return null;
  const separator = itemId.indexOf(':');
  if (separator <= 0 || separator === itemId.length - 1) return null;
  return {
    categoryType: itemId.slice(0, separator),
    poiId: itemId.slice(separator + 1),
  };
}

/**
 * When a coordinate sits on an attached stage destination, return that
 * destination. Used at routing time to recognise a segment endpoint that is
 * pinned to a POI/waypoint/highlight so it must NOT be snapped onto the
 * routing network.
 *
 * `trailStartDestination` must be passed by callers that route the plan's
 * FIRST segment. Attachments live in two separate slots — the day-keyed
 * stage-end pool and the singular trail start — and checking only the pool
 * leaves a pinned trail start unrecognised, so routing snaps it onto the
 * nearest way and the attachment is then pruned for no longer coinciding
 * with its item. Mirrors how resolveAttachedTrailStart pairs with
 * resolveAttachedDestinations everywhere else.
 */
export function attachedDestinationAtCoord(
  routes: { [routeKey: string]: Route },
  destinations: { [dayNumber: number]: StageDestination },
  coord: number[] | undefined,
  toleranceM: number = DESTINATION_ATTACHED_THRESHOLD_M,
  trailStartDestination: StageDestination | null = null
): StageDestination | null {
  if (!coord || coord.length < 2) return null;
  const attached = resolveAttachedDestinations(routes, destinations);
  for (const entry of Object.values(attached)) {
    if (haversineMeters(entry.destination.coordinates, coord) <= toleranceM) {
      return entry.destination;
    }
  }

  const attachedStart = resolveAttachedTrailStart(routes, trailStartDestination);
  if (attachedStart &&
      haversineMeters(attachedStart.destination.coordinates, coord) <= toleranceM) {
    return attachedStart.destination;
  }

  return null;
}

/**
 * Map a rendered map feature to a destination, when the feature is an
 * attachable item (map-data POI, waypoint or hike highlight). The feature
 * shape mirrors mapbox's queryRenderedFeatures output — only `layer.id`,
 * `source`, `properties` and Point `geometry` are read.
 */
export function extractAttachTarget(feature: {
  layer?: { id?: string };
  source?: string;
  properties?: { [key: string]: any };
  geometry?: { type?: string; coordinates?: number[] };
}): StageDestination | null {
  if (!feature || feature.geometry?.type !== 'Point') return null;
  const coords = feature.geometry.coordinates;
  if (!coords || coords.length < 2) return null;
  const coordinates: [number, number] = [coords[0], coords[1]];
  const props = feature.properties || {};
  const layerId = feature.layer?.id || '';
  const source = feature.source || '';

  // Hike highlights — one layer per highlight, id `highlight-${clientId}`
  if (layerId.startsWith('highlight-') && props.clientId) {
    return {
      itemType: 'highlight',
      itemId: String(props.clientId),
      name: props.title || 'Highlight',
      poiType: props.type || 'other',
      coordinates,
    };
  }

  // Waypoints — main layer (source 'waypoints') and individual layers
  // (source `waypoint-${n}`), both flagged/identified by clientId
  if (source.includes('waypoint') && props.clientId) {
    return {
      itemType: 'waypoint',
      itemId: String(props.clientId),
      name: props.title || 'Waypoint',
      poiType: props.type || 'other',
      coordinates,
    };
  }

  // Map-data POIs from the vector tiles (same test as isClickOnPOIHelper)
  if (source !== 'hover-point' && source.includes('poi') && props.id != null) {
    const categoryType = props.category_type || props.type;
    if (!categoryType) return null;
    return {
      itemType: 'poi',
      itemId: `${categoryType}:${props.id}`,
      name: props.name || String(props.type || categoryType),
      poiType: props.type || categoryType,
      coordinates,
    };
  }

  return null;
}

/**
 * Nearest attachable item among rendered features, by screen distance from
 * the cursor. `project` converts [lon, lat] to screen pixels (injected so
 * this stays map-free and testable).
 */
export function findNearestAttachTarget(
  features: Array<Parameters<typeof extractAttachTarget>[0]>,
  cursor: { x: number; y: number },
  project: (coordinates: [number, number]) => { x: number; y: number },
  maxDistancePx: number
): StageDestination | null {
  let best: StageDestination | null = null;
  let bestDistance = Infinity;
  for (const feature of features) {
    const target = extractAttachTarget(feature);
    if (!target) continue;
    const px = project(target.coordinates);
    const distance = Math.hypot(px.x - cursor.x, px.y - cursor.y);
    if (distance <= maxDistancePx && distance < bestDistance) {
      bestDistance = distance;
      best = target;
    }
  }
  return best;
}
